import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

const rand = (min, max) => Math.random() * (max - min) + min;
const fmt = (n, d = 2) => n.toFixed(d);

const MockShell = ({ title, badge, children, className = "" }) => (
  <div className={`mock-frame ${className}`} data-testid={`mock-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F7F9FA]/20 bg-[#17171B]">
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 bg-[#32A2C5]" aria-hidden="true" />
        <span className="w-2.5 h-2.5 bg-[#F7F9FA]/30" aria-hidden="true" />
        <span className="w-2.5 h-2.5 bg-[#F7F9FA]/30" aria-hidden="true" />
      </div>
      <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#F7F9FA]/70">{title}</p>
      {badge && (
        <span className="font-mono2 text-[10px] font-bold px-2 py-0.5 bg-[#32A2C5] text-[#101820] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-white tick-dot" aria-hidden="true" />
          {badge}
        </span>
      )}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

export const BidderStream = () => {
  const reduce = useReducedMotion();
  const [qps, setQps] = useState(48213);
  const [bids, setBids] = useState(() =>
    Array.from({ length: 5 }, (_, i) => ({ id: i, ch: "CTV", cpm: rand(4, 28), ms: rand(8, 14), win: Math.random() > 0.5 }))
  );

  useEffect(() => {
    if (reduce) return;
    const chs = ["CTV", "DISPLAY", "AUDIO", "DOOH", "NATIVE", "MOBILE", "VIDEO"];
    const t = setInterval(() => {
      setQps((q) => Math.max(40000, Math.min(56000, q + Math.round(rand(-1400, 1400)))));
      setBids((b) => [
        { id: Date.now(), ch: chs[Math.floor(Math.random() * chs.length)], cpm: rand(4, 28), ms: rand(8, 14), win: Math.random() > 0.45 },
        ...b.slice(0, 4),
      ]);
    }, 1600);
    return () => clearInterval(t);
  }, [reduce]);

  return (
    <MockShell title="RTB Bidder · Live Stream" badge="12ms median">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#F7F9FA]/50">Bid requests / sec</p>
          <p className="font-display font-black text-4xl tabular-nums" data-testid="mock-qps-counter">{qps.toLocaleString()}</p>
        </div>
        <div className="flex items-end gap-1 h-12" aria-hidden="true">
          {[35, 55, 40, 70, 50, 85, 60, 95, 75, 100].map((h, i) => (
            <span key={i} className="w-2 bg-[#32A2C5]" style={{ height: `${h}%`, opacity: 0.35 + (i / 10) * 0.65 }} />
          ))}
        </div>
      </div>
      <div className="space-y-1.5" data-testid="mock-bid-stream">
        {bids.map((b, i) => (
          <div key={b.id} className="flex items-center justify-between font-mono2 text-[11px] border border-[#F7F9FA]/15 px-3 py-1.5 bg-[#F7F9FA]/[0.03]" style={{ opacity: 1 - i * 0.18 }}>
            <span className="text-[#F7F9FA]/60 w-16">{b.ch}</span>
            <span className="tabular-nums">CPM ${fmt(b.cpm)}</span>
            <span className="tabular-nums text-[#F7F9FA]/60">{fmt(b.ms, 0)}ms</span>
            <span className={`font-bold ${b.win ? "text-emerald-400" : "text-[#F7F9FA]/40"}`}>{b.win ? "WON" : "LOST"}</span>
          </div>
        ))}
      </div>
    </MockShell>
  );
};

export const OutcomeAI = () => {
  const bars = [22, 30, 28, 42, 50, 47, 63, 74, 70, 88];
  return (
    <MockShell title="Outcome AI · Optimizer" badge="ROAS goal: 4.0x">
      <div className="flex items-end gap-1.5 h-28 mb-4" aria-hidden="true">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 flex flex-col justify-end h-full">
            <span className={`block w-full ${i >= bars.length - 3 ? "bg-[#32A2C5]" : "bg-[#F7F9FA]/25"}`} style={{ height: `${h}%` }} />
          </div>
        ))}
      </div>
      <div className="space-y-2 font-mono2 text-[11px]">
        {[
          ["CTV budget shift", "+18%", "text-emerald-400"],
          ["Frequency cap: Audio", "3 / wk", "text-[#F7F9FA]/70"],
          ["Predicted ROAS (7d)", "4.3x", "text-[#32A2C5] font-bold"],
        ].map(([k, v, c]) => (
          <div key={k} className="flex justify-between border-b border-[#F7F9FA]/10 pb-1.5">
            <span className="text-[#F7F9FA]/60">{k}</span>
            <span className={c}>{v}</span>
          </div>
        ))}
      </div>
    </MockShell>
  );
};

export const FeeTransparencyMock = () => (
  <MockShell title="Invoice · Line Items" badge="No hidden spread">
    <div className="space-y-2 font-mono2 text-[11px]">
      {[
        ["Media cost (at exchange price)", "$92,000.00"],
        ["Platform fee (8% disclosed)", "$7,360.00"],
        ["Data / tech surcharges", "$0.00"],
        ["Undisclosed margins", "$0.00"],
      ].map(([k, v]) => (
        <div key={k} className="flex justify-between border-b border-[#F7F9FA]/10 pb-1.5">
          <span className="text-[#F7F9FA]/60">{k}</span>
          <span className="tabular-nums">{v}</span>
        </div>
      ))}
      <div className="flex justify-between pt-1.5 text-sm font-bold">
        <span>Total invoice</span>
        <span className="text-[#32A2C5] tabular-nums">$99,360.00</span>
      </div>
    </div>
  </MockShell>
);

export const ChannelMatrix = () => (
  <MockShell title="Omnichannel Matrix" badge="1 workflow">
    <div className="grid grid-cols-4 gap-1.5" data-testid="mock-channel-matrix">
      {["DISPLAY", "VIDEO", "CTV", "NATIVE", "AUDIO", "DOOH", "MOBILE", "+PMP"].map((c, i) => (
        <div key={c} className={`border px-2 py-3 text-center font-mono2 text-[10px] font-bold tracking-wider ${i === 2 ? "bg-[#32A2C5] border-[#32A2C5] text-white" : "border-[#F7F9FA]/20 text-[#F7F9FA]/70"}`}>
          {c}
        </div>
      ))}
    </div>
    <div className="mt-3 flex justify-between font-mono2 text-[10px] text-[#F7F9FA]/50">
      <span>Unified pacing: ON</span>
      <span>Cross-channel frequency: ON</span>
    </div>
  </MockShell>
);

export const IdentityGraph = () => (
  <MockShell title="Identity Graph" badge="Consent-first">
    <svg viewBox="0 0 320 150" className="w-full" role="img" aria-label="Identity graph showing UID2, CRM and contextual nodes linked to a household node">
      <line x1="160" y1="75" x2="60" y2="35" stroke="#32A2C5" strokeWidth="2" />
      <line x1="160" y1="75" x2="60" y2="115" stroke="#F7F9FA" strokeOpacity="0.3" strokeWidth="2" />
      <line x1="160" y1="75" x2="260" y2="35" stroke="#F7F9FA" strokeOpacity="0.3" strokeWidth="2" />
      <line x1="160" y1="75" x2="260" y2="115" stroke="#32A2C5" strokeWidth="2" strokeDasharray="5 4" />
      <rect x="128" y="60" width="64" height="30" fill="#32A2C5" />
      <text x="160" y="79" textAnchor="middle" fill="#fff" fontSize="10" fontFamily="JetBrains Mono" fontWeight="700">YOU</text>
      {[[60, 35, "UID2"], [60, 115, "CRM"], [260, 35, "CONTEXT"], [260, 115, "CLEAN RM"]].map(([x, y, t]) => (
        <g key={t}>
          <rect x={x - 34} y={y - 13} width="68" height="26" fill="#17171B" stroke="#F7F9FA" strokeOpacity="0.3" />
          <text x={x} y={y + 4} textAnchor="middle" fill="#F7F9FA" fontSize="9" fontFamily="JetBrains Mono">{t}</text>
        </g>
      ))}
    </svg>
    <div className="mt-2 flex justify-between font-mono2 text-[10px] text-[#F7F9FA]/50">
      <span>Match rate: 84%</span>
      <span>Fallback: contextual AI</span>
    </div>
  </MockShell>
);

export const SpendCalculator = () => {
  const [spend, setSpend] = useState(250000);
  const riqFee = spend * 0.08;
  const legacyTake = spend * 0.35;
  const savings = legacyTake - riqFee;
  const money = (n) => "$" + Math.round(n).toLocaleString();
  return (
    <div className="card-brut p-6 sm:p-8" data-testid="spend-calculator">
      <p className="eyebrow mb-2">Interactive · Transparency Calculator</p>
      <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mb-6">What does a black box <span className="accent-word">cost you?</span></h3>
      <label className="label-riq" htmlFor="spend-slider">Monthly media spend</label>
      <input
        id="spend-slider"
        type="range"
        min="25000"
        max="2000000"
        step="25000"
        value={spend}
        onChange={(e) => setSpend(Number(e.target.value))}
        className="w-full accent-[#32A2C5] h-2"
        data-testid="spend-slider"
      />
      <p className="font-display font-black text-3xl mt-2 tabular-nums" data-testid="spend-value">{money(spend)}<span className="text-base font-bold text-[#4A5A63]"> /mo</span></p>
      <div className="grid sm:grid-cols-3 gap-3 mt-6 font-mono2 text-sm">
        <div className="border border-[#D8E2E6] p-4 bg-white">
          <p className="text-[10px] uppercase tracking-widest text-[#4A5A63]">RresponsIQ fee (8%)</p>
          <p className="font-bold text-xl mt-1 tabular-nums" data-testid="calc-riq-fee">{money(riqFee)}</p>
        </div>
        <div className="border border-[#D8E2E6] p-4 bg-white">
          <p className="text-[10px] uppercase tracking-widest text-[#4A5A63]">Typical black-box take (~35%)</p>
          <p className="font-bold text-xl mt-1 tabular-nums text-[#4A5A63] line-through decoration-[#32A2C5] decoration-2" data-testid="calc-legacy-fee">{money(legacyTake)}</p>
        </div>
        <div className="border border-[#D8E2E6] p-4 bg-[#32A2C5] text-[#101820]">
          <p className="text-[10px] uppercase tracking-widest opacity-80">You keep, every month</p>
          <p className="font-bold text-xl mt-1 tabular-nums" data-testid="calc-savings">{money(savings)}</p>
        </div>
      </div>
      <p className="mt-4 text-xs text-[#4A5A63]">Illustrative. Black-box estimate blends undisclosed margins, reseller markups and data surcharges typical of bundled-CPM DSPs.</p>
    </div>
  );
};
